import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2 } from 'lucide-react';
import type { MarketingRequest, MarketingRequestPatch } from '../../types/marketing';
import MarketingRequestDetailPanel from './MarketingRequestDetailPanel';
import MarketingRequestComments from './MarketingRequestComments';
import '../pm/TaskComments.css';
import '../ops/OpsCommentsPopout.css';

export interface MarketingRequestCommentsPopoutProps {
  request: MarketingRequest | null;
  highlightCommentId?: string | null;
  onHighlightCommentConsumed?: () => void;
  onUpdate: (id: string, updates: MarketingRequestPatch) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
  canDelete?: boolean;
}

const MarketingRequestCommentsPopout: React.FC<MarketingRequestCommentsPopoutProps> = ({
  request,
  highlightCommentId,
  onHighlightCommentConsumed,
  onUpdate,
  onDelete,
  onClose,
  canDelete = false,
}) => {
  const [commentCount, setCommentCount] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');

  useEffect(() => {
    setConfirmDelete(false);
    setEditingTitle(false);
    setTitleDraft(request?.title || '');
  }, [request?.id, request?.title]);

  useEffect(() => {
    if (!request?.id) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('tc-popout-open');
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('tc-popout-open');
    };
  }, [request?.id, onClose]);

  if (!request?.id) return null;

  const saveTitle = () => {
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== request.title) onUpdate(request.id, { title: trimmed });
    setEditingTitle(false);
  };

  return createPortal(
    <>
      <div className="tc-popout-backdrop" onClick={onClose} aria-hidden />
      <div
        className="tc-section tc-section--popout tc-section--ops-task mh-request-popout"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mreq-popout-title"
        onClick={e => e.stopPropagation()}
      >
        <div className="tc-popout-header">
          {editingTitle ? (
            <input
              id="mreq-popout-title"
              className="ops-popout-title-input"
              value={titleDraft}
              onChange={e => setTitleDraft(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={e => {
                if (e.key === 'Enter') saveTitle();
                if (e.key === 'Escape') {
                  setTitleDraft(request.title);
                  setEditingTitle(false);
                }
              }}
              autoFocus
            />
          ) : (
            <button
              type="button"
              id="mreq-popout-title"
              className="tc-popout-title ops-popout-title-btn"
              title={request.title}
              onClick={() => setEditingTitle(true)}
            >
              {request.title}
            </button>
          )}
          <span className="tc-popout-count">{commentCount}</span>
          {canDelete && (
            <div className="ops-popout-header-actions">
              {confirmDelete ? (
                <div className="ops-popout-delete-confirm">
                  <span>Delete?</span>
                  <button type="button" className="ops-popout-delete-yes" onClick={() => onDelete(request.id)}>
                    Yes
                  </button>
                  <button type="button" className="ops-popout-delete-no" onClick={() => setConfirmDelete(false)}>
                    No
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="ops-popout-delete-btn"
                  onClick={() => setConfirmDelete(true)}
                  title="Delete request"
                  aria-label="Delete request"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          )}
          <button
            type="button"
            className="tc-popout-close"
            onClick={onClose}
            title="Close (Esc)"
            aria-label="Close popup"
          >
            <X size={14} />
          </button>
        </div>

        <MarketingRequestDetailPanel request={request} onUpdate={onUpdate} />

        <div className="ops-popout-comments-section">
          <div className="ops-popout-comments-label">Comments</div>
          <div className="ops-popout-comments-wrap">
            <MarketingRequestComments
              key={request.id}
              embedded
              requestId={request.id}
              requestTitle={request.title}
              requestStatus={request.status}
              highlightCommentId={highlightCommentId}
              onHighlightConsumed={onHighlightCommentConsumed}
              onCommentCountChange={setCommentCount}
            />
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default MarketingRequestCommentsPopout;
